import { CalendarDays } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { allowedReleaseDate, changelogToday } from "../lib/changelog-date";
import { Button } from "./beui/Button";
import { WheelPicker } from "./beui/WheelPicker";

type CalendarDate = { year: number; month: number; day: number };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function daysInMonth(year: number, month: number) {
  // Calendar arithmetic preserves ISO years 0000–0099, which numeric Date
  // constructors otherwise reinterpret as 1900–1999.
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isoDate(date: CalendarDate) {
  return `${String(date.year).padStart(4, "0")}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

function initialDate(value: string): CalendarDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  const today = new Date();
  return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
}

function dateLabel(value: string) {
  if (!value) return "Choose date";
  const date = initialDate(value);
  return `${MONTHS[date.month - 1].slice(0, 3)} ${date.day}, ${String(date.year).padStart(4, "0")}`;
}

/** The form owns its ISO date. Wheel changes are temporary until Done; the
 * validation input retains required form behavior without opening a native picker. */
export function ChangelogDatePicker({ value, publishedDate, onChange, disabled = false }: { value: string; publishedDate?: string; onChange: (value: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const today = useManilaToday();
  const error = !value ? "Choose a release date." : !allowedReleaseDate(value, publishedDate, today) ? "Choose today or a later release date." : null;
  const validation = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  useLayoutEffect(() => { validation.current?.setCustomValidity(error ?? ""); }, [error]);
  return <div className="relative min-w-0">
    <span id={`${id}-label`} className="mb-1.5 block text-xs font-semibold text-navy">Release date</span>
    <Button ref={trigger} type="button" variant="outline" className="min-h-11 w-full justify-between rounded-xl border-line bg-white px-3.5 text-sm font-normal text-navy" pressScale={0.98}
      aria-labelledby={`${id}-label ${id}-value`} aria-haspopup="dialog" aria-expanded={open} aria-invalid={showError && !!error || undefined} aria-describedby={showError && error ? `${id}-error` : undefined}
      disabled={disabled} onClick={() => setOpen(true)}>
      <span id={`${id}-value`}>{dateLabel(value)}</span><CalendarDays className="h-4 w-4 shrink-0 text-mut" aria-hidden="true" />
    </Button>
    <input ref={validation} className="sr-only" type="text" name="releaseDate" required tabIndex={-1} aria-hidden="true" value={value} disabled={disabled} onChange={() => undefined}
      onInvalid={(event) => { event.preventDefault(); setShowError(true); trigger.current?.focus(); }} />
    {showError && error && <p id={`${id}-error`} role="alert" className="mt-2 text-xs text-brand-reddark">{error}</p>}
    {publishedDate && publishedDate < today && publishedDate !== value && <Button type="button" variant="ghost" className="mt-1 min-h-11 justify-start px-0 text-xs text-navy" pressScale={0.98} disabled={disabled}
      aria-label={`Use published date, ${dateLabel(publishedDate)}`} onClick={() => { setShowError(false); onChange(publishedDate); }}>Use published date</Button>}
    {open && <DateWheels value={value} today={today} onCancel={() => setOpen(false)} onDone={(next) => { setShowError(false); onChange(next); setOpen(false); }} />}
  </div>;
}

function useManilaToday() {
  const [today, setToday] = useState(changelogToday);
  useEffect(() => {
    const refresh = () => setToday(changelogToday());
    // Manila has no seasonal clock change; refresh at its next midnight and
    // on return to this tab, independently of the device's local timezone.
    const nextMidnight = Date.parse(`${today}T00:00:00+08:00`) + 86_400_000;
    const timer = window.setTimeout(refresh, Math.max(1, nextMidnight - Date.now() + 10));
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.clearTimeout(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [today]);
  return today;
}

// Posting shares the typed input's 1000–9999 range; only Changelog supplies its Manila date floor.
export function DateWheels({ value, today, label = "Release date", onCancel, onDone }: { value: string; today?: string; label?: string; onCancel: () => void; onDone: (value: string) => void }) {
  const floor = today || "1000-01-01";
  const [draft, setDraft] = useState(() => initialDate(today && (!value || value < today) ? today : value));
  const [rollover, setRollover] = useState(false);
  const minimum = useMemo(() => initialDate(floor), [floor]);
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const id = useId();
  const firstMonth = draft.year === minimum.year ? minimum.month : 1;
  const firstDay = draft.year === minimum.year && draft.month === minimum.month ? minimum.day : 1;
  const monthOptions = useMemo(() => MONTHS.map((label, index) => ({ label, value: String(index + 1) })).slice(firstMonth - 1), [firstMonth]);
  const dayOptions = useMemo(() => Array.from({ length: daysInMonth(draft.year, draft.month) - firstDay + 1 }, (_, index) => String(firstDay + index)), [draft.year, draft.month, firstDay]);
  const yearOptions = useMemo(() => Array.from({ length: 10000 - minimum.year }, (_, index) => String(minimum.year + index).padStart(4, "0")), [minimum.year]);

  useEffect(() => {
    if (!today || isoDate(draft) >= today) return;
    setDraft(initialDate(today)); setRollover(true);
  }, [today, draft]);

  useEffect(() => {
    const modal = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modal?.showModal();
    heading.current?.focus({ preventScroll: true });
    return () => { modal?.close(); if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);

  function changeDate(change: Partial<CalendarDate>) {
    setDraft((previous) => {
      const next = { ...previous, ...change };
      const clamped = { ...next, day: Math.min(next.day, daysInMonth(next.year, next.month)) };
      return isoDate(clamped) < floor ? initialDate(floor) : clamped;
    });
  }

  function trapFocus(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== "Tab") return;
    const controls = [...(dialog.current?.querySelectorAll<HTMLElement>("button:not(:disabled):not([tabindex='-1']), input:not(:disabled), [tabindex='0']") ?? [])].filter((control) => control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  return <dialog ref={dialog} aria-labelledby={`${id}-title`} onKeyDown={trapFocus}
    onCancel={(event) => { event.preventDefault(); onCancel(); }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onCancel();
    }}
    className="m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-sm overflow-y-auto rounded-3xl border border-line bg-white p-5 shadow-xl backdrop:bg-navy/50 sm:p-6">
    <h2 ref={heading} id={`${id}-title`} tabIndex={-1} className="text-lg font-bold text-navy outline-none">{label}</h2>
    {today && value && value < today && <p className="mt-2 text-xs leading-5 text-mut">Current: {dateLabel(value)}. To change it, choose today or later.</p>}
    {rollover && <p role="status" className="mt-2 text-xs text-mut">Today changed in Asia/Manila. Choose today or later.</p>}
    <div className="mt-4 grid grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-2">
      <div className="min-w-0"><WheelPicker aria-label="Month" options={monthOptions} value={String(draft.month)} onValueChange={(month) => changeDate({ month: Number(month) })} itemHeight={44} sound /></div>
      <div className="min-w-0"><WheelPicker aria-label="Day" options={dayOptions} value={String(draft.day)} onValueChange={(day) => changeDate({ day: Number(day) })} itemHeight={44} sound /></div>
      <div className="min-w-0"><WheelPicker aria-label="Year" options={yearOptions} value={String(draft.year).padStart(4, "0")} onValueChange={(year) => changeDate({ year: Number(year) })} itemHeight={44} sound /></div>
    </div>
    <div className="mt-5 grid grid-cols-2 gap-3">
      <Button type="button" variant="outline" className="min-h-11 bg-white" pressScale={0.98} onClick={onCancel}>Cancel</Button>
      <Button type="button" className="min-h-11 bg-navy text-white hover:bg-navy-500" pressScale={0.98} onClick={() => { const currentMinimum = today ? changelogToday() : floor; if (isoDate(draft) < currentMinimum) { setDraft(initialDate(currentMinimum)); setRollover(true); return; } onDone(isoDate(draft)); }}>Done</Button>
    </div>
  </dialog>;
}
